import styled from 'styled-components';
import theme from 'theme/theme';
import media from 'utils/MediaQueries';
import {
	PAGE_MAX,
	PAGE_PAD,
	PAGE_PAD_MOBILE,
} from 'components/Section/styled';

/**
 * Same box model as Intro (max-width + padding), not an independently
 * narrower column — two differently-sized boxes each centered on their own
 * do not share a left edge, which would misalign this section's text from
 * the intro above it and the logo in the TopBar.
 */
const Content = styled.div`
	width: 100%;
	max-width: ${PAGE_MAX};
	padding: 0 ${PAGE_PAD} 100px;

	${media.tablet`
		padding: 0 ${PAGE_PAD_MOBILE} 70px;
	`};
`;

/**
 * Caps prose measure for readability without narrowing Content itself, so
 * this stays a block inside the shared, aligned Content box rather than a
 * second independently-centered box.
 */
const Prose = styled.div`
	max-width: 720px;
`;

/**
 * The steps panel is the one thing Google's requirement calls "prominent" —
 * it gets the boldest surface on the page, stronger than the Callout used
 * for secondary asides on the Privacy/Terms pages.
 */
const StepsPanel = styled.div`
	background-color: rgba(255, 255, 255, 0.045);
	border: 1px solid ${theme.rule};
	border-left: 3px solid ${theme.primary1};
	border-radius: 8px;
	padding: 28px 26px 26px 23px;
	margin: 0 0 40px 0;

	${media.phone`
		padding: 22px 20px 22px 17px;
	`};
`;

const StepsTitle = styled.h2`
	font-family: ${theme.headerFont};
	color: ${theme.chalk};
	font-size: 21px;
	margin: 0 0 18px 0;
`;

const Steps = styled.ol`
	list-style: none;
	counter-reset: step;
	margin: 0;
	padding: 0;
`;

/**
 * The counter badge is positioned rather than laid out as a flex sibling.
 * With `display: flex` on the <li>, every inline child — a <strong>, a link —
 * becomes its own anonymous flex item and gets laid out as a column beside
 * the text instead of flowing inside the sentence, which pushed content past
 * the container on narrow screens.
 */
const Step = styled.li`
	counter-increment: step;
	position: relative;
	padding-left: 40px;
	min-height: 26px;
	font-size: 15px;
	line-height: 1.7;
	color: ${theme.chalk};
	opacity: 0.88;
	overflow-wrap: anywhere;

	& + & {
		margin-top: 16px;
	}

	&::before {
		content: counter(step);
		position: absolute;
		left: 0;
		top: 0;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		background-color: rgba(77, 172, 116, 0.16);
		color: ${theme.primary1};
		font-family: ${theme.monoFont};
		font-size: 13px;
		font-weight: 700;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	strong {
		color: ${theme.chalk};
		opacity: 1;
	}
`;

const SectionTitle = styled.h2`
	font-family: ${theme.headerFont};
	color: ${theme.chalk};
	font-size: 19px;
	margin: 40px 0 14px 0;
`;

export { Content, Prose, StepsPanel, StepsTitle, Steps, Step, SectionTitle };
